import axios from "axios"

// Pre-auth endpoints: aucun Sanctum token requis, le lead n'a pas encore de compte.

// Map frontend camelCase state keys → snake_case wire format expected by Laravel.
const camelToSnakeAnswers = (data) => {
    if (!data) return data
    const mapping = {
        studentClass: "student_class",
        studentClassRentree: "student_class_rentree",
        niveauActuel: "niveau_actuel",
        hoursPerWeek: "hours_per_week",
        slotIds: "slot_ids",
        subjectHours: "subject_hours",
        startDate: "start_date",
        withCommitment: "with_commitment",
    }
    return Object.fromEntries(
        Object.entries(data)
            // Drop undefined/null values (e.g. an absent subject_hours) so the
            // backend's `sometimes` rules treat them as not sent.
            .filter(([, v]) => v !== undefined && v !== null)
            .map(([k, v]) => [mapping[k] || k, v])
    )
}

/**
 * Hydrate le funnel Cours d'été depuis un submissionGUID HubSpot (redirect LP).
 * Réutilise le même formulaire `subscription_1_to_5` que le brevet/fast-track
 * (tout le tracking Ads est déjà câblé). Renvoie funnel_state_id + user pré-rempli.
 */
function initFromHubSpot({}, data) {
    return axios.post(`/api/v1/summer-funnel/init`, data)
}

function startFunnel({}, data) {
    return axios.post(`/api/v1/summer-funnel/start`, data)
}

function updateAnswers({ stateId }, data) {
    return axios.put(
        `/api/v1/summer-funnel/${stateId}/answers`,
        camelToSnakeAnswers(data)
    )
}

function createCheckoutSession({ stateId }, data) {
    return axios.post(
        `/api/v1/summer-funnel/${stateId}/create-checkout-session`,
        data
    )
}

function getFunnelState({ stateId }) {
    return axios.get(`/api/v1/summer-funnel/${stateId}`)
}

/**
 * « Lien sans 1 € » : souscrit l'abo Cours d'été directement sur le moyen de
 * paiement déjà au dossier (0 € aujourd'hui, 1ᵉʳ prélèvement au 1ᵉʳ du mois
 * d'entrée). Derrière `auth_or_email` → on passe `user` (base64 email du lead) en
 * query param pour prouver l'identité. Réponse 422 `no_payment_method` quand
 * aucune carte n'est au dossier (le front retombe sur le flux 1 €).
 */
function subscribeOnFile({ stateId }, body, queryParams) {
    return axios.post(
        `/api/v1/summer-funnel/${stateId}/subscribe-on-file`,
        body || {},
        { params: queryParams }
    )
}

/**
 * Matières proposables pour la classe — celles qui ont des chapitres validés,
 * déjà triées par rank côté backend. Réponse : { data: [{ id, name, slug,
 * icon_path, color, rank }] }.
 */
function getSubjects({ studentClass }) {
    return axios.get(`/api/v1/summer-funnel/subjects`, {
        params: { student_class: studentClass },
    })
}

/**
 * Programme personnalisé planifié (curriculum-driven, calendrier réel). Le body
 * est mappé camel→snake (inclut `slot_ids` = les créneaux choisis).
 * Réponse : { data: {
 *   weeks, hours_per_week,
 *   subjects: [{ id, name, icon_path, weekly_hours }],           // légende
 *   schedule: [{ week_index, week_start, sessions: [
 *     { date, subject: { id, name, icon_path },
 *       chapter: { id, name, theme, is_rentree } } ] }],
 * } }.
 */
function getProgram({ stateId }, data) {
    return axios.post(
        `/api/v1/summer-funnel/${stateId}/program`,
        camelToSnakeAnswers(data)
    )
}

/**
 * Options de date de démarrage pour la sélection courante (créneaux + mois +
 * variante d'engagement) : jusqu'à 3 choix dans le même mois d'entrée, chacun
 * avec le montant proratisé de la 1ʳᵉ facture (prélevée le jour de la 1ʳᵉ
 * séance). Source UNIQUE des dates/montants — le front ne recalcule jamais la
 * règle. Réponse : { data: { options: [{ start_date, first_session,
 * sessions_remaining, first_charge_amount_cents }], selected_start_date,
 * monthly_price_cents, recurring_start_date } }.
 */
function getStartOptions({ stateId }, data) {
    return axios.post(
        `/api/v1/summer-funnel/${stateId}/start-options`,
        camelToSnakeAnswers(data)
    )
}

/**
 * Tuteurs ("masteurs") qui correspondent au profil de l'enfant — étape "aha
 * moment tuteur humain" entre le programme et le checkout. Le backend réutilise
 * la couverture de matching (un masteur "compte" s'il couvre tout le panier) pour
 * le COMPTE, et un pool vitrine (vraies photos) pour les cartes. Comme `program`,
 * le body porte le panier : `subjects` (ids) + `student_class` — mappés camel→
 * snake. Réponse : { data: { count, total, showcase: [{ id, display_name,
 * avatar, study_institution, formation, subjects:[string],
 * strengths:[{ icon, label }] }], more_avatars:[string] } }. `more_avatars` =
 * jusqu'à 5 URLs de visages d'AUTRES masteurs (pile "et N autres"), hors vitrine.
 *
 * Étape NON bloquante : en cas d'erreur/vide, l'écran retombe sur un fallback
 * gracieux (cf. SummerTutorsScreen) et le CTA continue vers le checkout.
 */
function getMatchingTutors({ stateId }, data) {
    return axios.post(
        `/api/v1/summer-funnel/${stateId}/matching-tutors`,
        camelToSnakeAnswers(data)
    )
}

/**
 * Reprise d'un funnel Cours d'été depuis un lien magique émis par le BO.
 *
 * Le BO copie `/summer/resume?user=<base64email>` pour un lead chaud. Le front
 * lit le param `user` et appelle cet endpoint derrière `auth_or_email` ; le
 * backend renvoie le dernier summer FastTrackState pour qu'on hydrate le Context
 * et qu'on navigation.replace vers la bonne étape. Pendant du brevet `resume`.
 *
 * Nommé `resumeSummer` (et non `resume`) car le reducer api indexe le state par
 * `fonction.name` — un `resume` homonyme entrerait en collision avec le `resume`
 * du brevetFunnelService sur `state.api.resume`.
 */
function resumeSummer({}, {}, queryParams) {
    return axios.get(`/api/v1/summer-funnel/resume`, { params: queryParams })
}

export const summerFunnelService = {
    initFromHubSpot,
    startFunnel,
    updateAnswers,
    createCheckoutSession,
    subscribeOnFile,
    getFunnelState,
    getSubjects,
    getProgram,
    getStartOptions,
    getMatchingTutors,
    resumeSummer,
}
